import { ILinkedModel, ModelTranslationStatus } from "../../../responses/linkedModel";
import { createModelSelectionLock } from "../viewer-utils/viewerModelSelectionLock";
import eventBus from "../eventBus/eventDispatcher";
import repo from "../../../Repository";

export class LinkedModel {
    private _isLoading = false;
    private _loaded = false;
    private model: Autodesk.Viewing.Model | null = null;
    private waitHandle: number | undefined;
    private _isVisible = false;

    constructor(
        private readonly linkedModelSource: ILinkedModel,
        private readonly viewer: Autodesk.Viewing.Viewer3D,
        private readonly hiddenRevitCategories: number[],
        private readonly onModelLoaded: () => void,
    ) {
        this.onModelDocumentLoaded = this.onModelDocumentLoaded.bind(this);
    }

    get id(): string {
        return this.linkedModelSource.id;
    }

    get fileName(): string {
        return this.linkedModelSource.fileName;
    }

    get status(): ModelTranslationStatus {
        return this.linkedModelSource.status;
    }

    get origin(): THREE.Vector3 {
        return new THREE.Vector3().copy(this.linkedModelSource.origin);
    }

    get createdAt(): Date {
        return this.linkedModelSource.createdAt;
    }

    get translationProgressPercentage(): number {
        return this.linkedModelSource.translationProgressPercentage;
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    get loaded(): boolean {
        return this._loaded;
    }

    get isVisible(): boolean {
        return this._isVisible;
    }

    load() {
        if (this.status === ModelTranslationStatus.Failed) {
            return;
        }

        this._isLoading = true;

        if (this.status === ModelTranslationStatus.Ready) {
            this.loadModel();
        } else {
            this.waitForModelTranslation();
        }
    }

    unload() {
        if (this.loaded && this.model) {
            this.viewer.unloadModel(this.model);
        }

        this.dispose();
    }

    canLoad(): boolean {
        return this.status === ModelTranslationStatus.Ready;
    }

    dispose() {
        if (this.waitHandle !== undefined) {
            window.clearTimeout(this.waitHandle);
        }

        this.model = null;
        this._isLoading = false;
        this._loaded = false;
        this._isVisible = false;
    }

    setVisible(isVisible: boolean) {
        if (!(this.model && this.loaded)) {
            return;
        }

        if (isVisible) {
            this.viewer.showModel(this.model, false);
        } else {
            this.viewer.hideModel(this.model);
        }

        this._isVisible = isVisible;
    }

    highlight() {
        if (!(this.model && this.loaded && this.isVisible)) {
            return;
        }

        this.viewer.select(this.model.getRootId(), this.model, Autodesk.Viewing.SelectionType.OVERLAYED);
    }

    setOrigin(origin: THREE.Vector3) {
        if (!this.model) {
            return;
        }

        this.linkedModelSource.origin = origin;

        this.model.setGlobalOffset(this.getGlobalOffset());

        if (!this._isVisible) {
            return;
        }

        this.viewer.select([], this.model);
        this.viewer.impl.invalidate(true, true, false);
    }

    private loadModel() {
        const urn = `urn:${this.linkedModelSource.urn}`;

        Autodesk.Viewing.Document.load(urn, this.onModelDocumentLoaded, (errCode, errMsg) =>
            console.error(`[Linked model] Failed to load manifest [${errCode}] ${errMsg}. Urn: ${urn}`),
        );
    }

    private waitForModelTranslation() {
        const interval = 5000;

        const getInterval = () => interval + Math.floor(Math.random() * 300);

        const checkIfTranslated = async () => {
            const response = await repo.findLinkedModelsByIds([this.id]);

            if (response.isSuccess) {
                const linkedModelSource = response.items[0];

                this.update(linkedModelSource);

                switch (linkedModelSource.status) {
                    case ModelTranslationStatus.Ready:
                        this.waitHandle = undefined;
                        this.load();

                        return;

                    case ModelTranslationStatus.Failed:
                        this.waitHandle = undefined;
                        return;

                    default:
                        break;
                }
            }

            this.waitHandle = window.setTimeout(checkIfTranslated, getInterval());
        };

        this.waitHandle = window.setTimeout(checkIfTranslated, getInterval());
    }

    private async onModelDocumentLoaded(doc: Autodesk.Viewing.Document) {
        const root = doc.getRoot();

        const geometry = root.get3DModelNodes()[0] || root.getDefaultGeometry();

        if (geometry.is3D()) {
            this.model = await this.viewer.loadDocumentNode(doc, geometry, {
                applyRefPoint: true,
                loadAsHidden: true,
                globalOffset: this.getGlobalOffset(),
                keepCurrentModels: true,
                applyScaling: "ft",
                filter: {
                    property_query: {
                        $not: {
                            $or: this.hiddenRevitCategories.map(x => ({ "?CategoryId": x })),
                        },
                    },
                },
            });

            await this.viewer.waitForLoadDone({
                geometry: true,
                hidden: true,
                onlyModels: this.model,
            });

            this.model.setPlacementTransform(this.viewer.model.getPlacementTransform());
            this.viewer.showModel(this.model, false);
            this.viewer.impl.invalidate(true, true, false);

            const selectionLock = await createModelSelectionLock(this.viewer, this.model)();

            selectionLock.lock();
        }

        this._isLoading = false;
        this._loaded = true;
        this._isVisible = true;
        this.onModelLoaded();
    }

    private update(newSource: ILinkedModel) {
        if (
            this.linkedModelSource.status === newSource.status &&
            this.linkedModelSource.translationProgressPercentage === newSource.translationProgressPercentage
        ) {
            return;
        }

        this.linkedModelSource.status = newSource.status;
        this.linkedModelSource.translationProgressPercentage = newSource.translationProgressPercentage;

        eventBus.dispatchEvent({
            type: "Dextall.LinkedModels.ModelUpdated",
            payload: {
                id: this.id,
                status: newSource.status,
                translationProgressPercentage: newSource.translationProgressPercentage,
            },
        });
    }

    private getGlobalOffset(): THREE.Vector3 {
        return new THREE.Vector3().copy(this.origin).negate().add(this.viewer.model.getGlobalOffset());
    }
}
